//import THREE from "three";
import OrbitControlsExtended, { EPS, STATE } from "./orbit-controls-extended";
import Utils from "../utils";
import { MouseHoverRotation } from "./mouse-hover-rotation";
import RenderManager from "../rendering/render-manager";
import CameraTweenTrack from "./camera-tween-track";

const RAYCAST_DISTANCE = Infinity;
const RAYCAST_TARGET_DISTANCE = 1e-5;
const ALLOWED_ZOOM_DISTANCE = 1e-10;
const MIN_NEAR = 2e-3;
const NEAR = 0.001;
const MIDPOINT_DISTANCE = 1e-1;
const GLOBAL_DAMPING_FACTOR = 2;
const EPS_1DEG = THREE.Math.DEG2RAD;

const raycaster = new THREE.Raycaster();
const g_point = new THREE.Vector3();
const g_pointOffset = new THREE.Vector3();
const g_forward = new THREE.Vector3();
const g_itsct = new THREE.Vector2();
const g_lastSetByUserPosition = {
    target: new THREE.Vector3(),
    position: new THREE.Vector3()
};

class PhysicalCameraControls extends OrbitControlsExtended {
    constructor(object, dom, scene, filter) {
        super(object, dom);

        let spherical = this._spherical;
        let sphericalDelta = this._sphericalDelta;
        let zoomChanged = this._zoomChanged;
        let scope = this;
        let distanceDelta = this._distanceDelta;
        let zoomDelta = this._zoomDelta;
        let panOffset = this._panoffset;
        let changeEvent = this._changeEvent;

        let applyPanOffset = this._applyPanOffset;
        let state = this._state;

        this._scene = scene;
        this._filter = filter;

        const newPosition = new THREE.Vector3();

        this._helper = new THREE.Mesh(new THREE.BoxBufferGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xff00ff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false }));
        if (Utils.queryParams.has("physcam_helper"))
            scene.add(this._helper);
        this._helper.scale.set(0.2, 0.2, 0.2);
        this.wasLastClipped = false;
        // this._helper.scale.set(this.object.near, this.object.near, this.object.near).multiplyScalar(1);

        const rotateLeft = this.rotateLeft;
        const getAutoRotationAngle = this.getAutoRotationAngle;

        let userAction = false;
        let userActionDamping = false;
        let holdAutoRotateTill = 0;
        let autoRotationDelay = 3000;

        let targetSurfaceDistance = 0;

        let lastSave = false;

        this.isMovingWaiting = () => userAction || userActionDamping || lastSave;

        this.cancelDamping = function () {
            userAction = false;
            userActionDamping = false;
        }

        let mouseHoverRotation = new MouseHoverRotation(dom);

        this.resetDistance = function () {
            targetSurfaceDistance = 0;
        }

        this.getSphericalCoordinates = (function () {
            let offset = new THREE.Vector3();

            // so camera.up is the orbit axis
            let quat = new THREE.Quaternion().setFromUnitVectors(object.up, new THREE.Vector3(0, 1, 0));

            let orbitTargetRel = new THREE.Vector3();
            let matrixInverse = new THREE.Matrix4();

            let tmpObject = new THREE.Camera();

            return function getSphericalCoordinates(spherical) {
                // get the orbit center position relative to camera
                object.updateMatrix();
                object.updateMatrixWorld();
                matrixInverse.getInverse(scope.object.matrix);
                orbitTargetRel.copy(scope.target).applyMatrix4(matrixInverse);

                // push camera along its plane to focus on orbit center
                tmpObject.position.set(orbitTargetRel.x, orbitTargetRel.y, 0).applyMatrix4(object.matrix);
                tmpObject.updateMatrix();

                offset.copy(tmpObject.position).sub(scope.target);

                // rotate offset to "y-axis-is-up" space
                offset.applyQuaternion(quat);

                // angle from z-axis around y-axis
                spherical.setFromVector3(offset);
            };
        })();

        this.update = function () {

            let offset = new THREE.Vector3();

            let zoom = object.zoom;

            let distance;

            // so camera.up is the orbit axis
            let quat = new THREE.Quaternion().setFromUnitVectors(object.up, new THREE.Vector3(0, 1, 0));
            let quatInverse = quat.clone().inverse();

            let lastPosition = new THREE.Vector3();
            let lastQuaternion = new THREE.Quaternion();

            let orbitTargetRel = new THREE.Vector3();
            let matrixInverse = new THREE.Matrix4();

            let tmpObject = new THREE.Camera();

            let getThetaClosestToRange = (theta, minAngle, maxAngle) => {
                if (!isFinite(minAngle) || !isFinite(maxAngle)) return theta;

                let midAngle = (minAngle + maxAngle) * 0.5;
                theta -= midAngle;

                let thetaTurns = theta / (2 * Math.PI);
                thetaTurns -= Math.round(thetaTurns)
                theta = thetaTurns * (2 * Math.PI);

                theta += midAngle;
                return theta;
            };

            return function update() {
                const isOrbitMode = Q3.controls === scope;
                const _1deg = THREE.Math.DEG2RAD;

                let minDistance, maxDistance;
                let minAzimuthAngle, maxAzimuthAngle;
                let minPolarAngle, maxPolarAngle;

                if (isOrbitMode) {
                    minDistance = scope.minDistance;
                    maxDistance = scope.maxDistance;
                    minAzimuthAngle = scope.minAzimuthAngle;
                    maxAzimuthAngle = scope.maxAzimuthAngle;
                    minPolarAngle = scope.minPolarAngle;
                    maxPolarAngle = scope.maxPolarAngle;
                } else {
                    minAzimuthAngle = -Infinity;
                    maxAzimuthAngle = Infinity;
                    minDistance = 0;
                    maxDistance = Infinity;
                    minPolarAngle = _1deg;
                    maxPolarAngle = Math.PI - _1deg;
                    scope._panLimits = null;
                    scope._panLimitsInv = null;
                }


                scope._helper.position.copy(scope.lookTarget ? scope.lookTarget : scope.target);

                // get the orbit center position relative to camera
                object.updateMatrix();
                object.updateMatrixWorld();
                matrixInverse.getInverse(scope.object.matrix);
                orbitTargetRel.copy(scope.target).applyMatrix4(matrixInverse);

                // push camera along its plane to focus on orbit center
                tmpObject.position.set(orbitTargetRel.x, orbitTargetRel.y, 0).applyMatrix4(object.matrix);
                tmpObject.updateMatrix();

                offset.copy(tmpObject.position).sub(scope.target);

                distance = offset.length();

                // rotate offset to "y-axis-is-up" space
                offset.applyQuaternion(quat);

                // angle from z-axis around y-axis
                spherical.setFromVector3(offset);

                if (scope.autoRotate && state.value === STATE.NONE && holdAutoRotateTill < Date.now()) {
                    CameraTweenTrack.finishTweens();
                    rotateLeft(getAutoRotationAngle());
                }

                const hover = mouseHoverRotation.update();

                spherical.theta = getThetaClosestToRange(spherical.theta, minAzimuthAngle, maxAzimuthAngle);
                spherical.theta += sphericalDelta.theta + hover.theta;
                spherical.phi += sphericalDelta.phi + hover.phi;

                if (Q3.editMode && !Q3.isTransitioning && Q3.physicalCameraControls === Q3.controls && (spherical.theta - EPS_1DEG > maxAzimuthAngle || spherical.theta + EPS_1DEG < minAzimuthAngle)) {
                    scope.showSnackBar("Horizontal limit reached");
                }

                // restrict theta to be between desired limits
                spherical.theta = Math.max(minAzimuthAngle, Math.min(maxAzimuthAngle, spherical.theta));

                if (Q3.editMode && !Q3.isTransitioning && Q3.physicalCameraControls === Q3.controls && (spherical.phi - EPS_1DEG > maxPolarAngle || spherical.phi + EPS_1DEG < minPolarAngle)) {
                    scope.showSnackBar("Vertical limit reached");
                }

                // restrict phi to be between desired limits
                spherical.phi = Math.max(minPolarAngle, Math.min(maxPolarAngle, spherical.phi));

                spherical.makeSafe();

                spherical.radius -= targetSurfaceDistance;
                spherical.radius *= (1 + distanceDelta.value);
                spherical.radius += targetSurfaceDistance;

                if (Q3.editMode && !Q3.isTransitioning && Q3.physicalCameraControls === Q3.controls && (spherical.radius - EPS_1DEG > maxDistance || spherical.radius + EPS_1DEG < minDistance)) {
                    scope.showSnackBar("Zoom limit reached");
                }

                // restrict radius to be between desired limits
                spherical.radius = Math.max(minDistance, Math.min(maxDistance, spherical.radius), targetSurfaceDistance);

                // move target to panned location
                applyPanOffset(panOffset);

                offset.setFromSpherical(spherical);

                // rotate offset back to "camera-up-vector-is-up" space
                offset.applyQuaternion(quatInverse);

                tmpObject.position.copy(scope.target).add(offset);

                tmpObject.lookAt(scope.target);
                // console.log("targ:", scope.target.toArray().map(x => x.toFixed(3)).join(", "), "|", "quat:", tmpObject.quaternion.toArray().map(x => x.toFixed(3)).join(", "));

                // push camera along its plane to put the orbit center where it was on screen
                tmpObject.updateMatrix();
                newPosition.set(-orbitTargetRel.x, -orbitTargetRel.y, 0).applyMatrix4(tmpObject.matrix);

                let distToTarget = newPosition.distanceToSquared(scope.target);

                targetSurfaceDistance = 0;

                if (distToTarget > ALLOWED_ZOOM_DISTANCE && !Q3.isTransitioning) {
                    const point = g_point.set(0, 0, 0);

                    if (scope._pickTarget(point)) {

                        const pointOffset = g_pointOffset.subVectors(point, this.target);
                        const forward = this.object.getWorldDirection(g_forward);

                        targetSurfaceDistance = -pointOffset.dot(forward) + this.object.near * 1.5;
                    }
                }

                const sign = Math.sign(targetSurfaceDistance) || 1;
                targetSurfaceDistance = Math.max(targetSurfaceDistance, sign * ALLOWED_ZOOM_DISTANCE);

                scope.object.quaternion.copy(tmpObject.quaternion);
                // console.log(scope.object.quaternion.toArray().map(x => x.toFixed(3)).join(", "));
                // console.log(scope.object.rotation.toArray().slice(0, 3).map(x=>(x*THREE.Math.RAD2DEG).toFixed(3)).join(", "))
                scope.object.position.copy(newPosition);

                scope._updateCamera();

                scope.object.updateMatrix();

                // update the camera focus point if a separate one from orbit center is set
                if (scope.lookTarget) {
                    scope.lookTarget.set(0, 0, orbitTargetRel.z).applyMatrix4(scope.object.matrix);
                }

                if (Math.abs(zoomDelta.value) > EPS * distance) { //zoomDelta is only changed for orthographic camera
                    zoom = Math.min(scope.maxZoom, Math.max(scope.minZoom, scope.object.zoom * (1 - zoomDelta.value)));

                    if (zoom !== scope.object.zoom) {
                        scope.object.zoom = zoom;
                        scope.object.updateProjectionMatrix();
                        zoomChanged.value = true;
                    }
                }

                lastSave = false;

                if (scope.enableDamping === true) {
                    const rotateDampingFactor = scope.rotateDampingFactor * GLOBAL_DAMPING_FACTOR;
                    const panDampingFactor = scope.panDampingFactor * GLOBAL_DAMPING_FACTOR;
                    const zoomDampingFactor = scope.zoomDampingFactor * GLOBAL_DAMPING_FACTOR;

                    sphericalDelta.theta *= (1 - rotateDampingFactor);
                    sphericalDelta.phi *= (1 - rotateDampingFactor);
                    panOffset.multiplyScalar(1 - panDampingFactor);
                    distanceDelta.value *= (1 - zoomDampingFactor);
                    zoomDelta.value *= (1 - zoomDampingFactor);

                    if (userActionDamping) {
                        const epsDamping = 1e-4;
                        if (
                            Math.abs(sphericalDelta.theta) < epsDamping &&
                            Math.abs(sphericalDelta.phi) < epsDamping &&

                            Math.abs(panOffset.x) < epsDamping &&
                            Math.abs(panOffset.y) < epsDamping &&
                            Math.abs(panOffset.z) < epsDamping &&

                            Math.abs(distanceDelta.value) < epsDamping &&
                            Math.abs(zoomDelta.value) < epsDamping
                        ) {
                            userActionDamping = false;
                            lastSave = true;
                        }
                    }
                } else {
                    sphericalDelta.set(0, 0, 0);
                    panOffset.set(0, 0, 0);
                    distanceDelta.value = 0;
                    zoomDelta.value = 0;
                }

                if (scope.isMovingWaiting()) {
                    g_lastSetByUserPosition.target.copy(scope.target);
                    g_lastSetByUserPosition.position.copy(scope.object.position);
                    scope.lastSetByUserPosition = g_lastSetByUserPosition;
                }

                // update condition is:
                // min(camera displacement, camera rotation in radians)^2 > EPS
                // using small-angle approximation cos(x/2) = 1 - x^2 / 8

                if (zoomChanged.value ||
                    lastPosition.distanceToSquared(scope.object.position) > EPS * distance ||
                    8 * (1 - lastQuaternion.dot(scope.object.quaternion)) > EPS * distance) {

                    scope.dispatchEvent(changeEvent);

                    lastPosition.copy(scope.object.position);
                    lastQuaternion.copy(scope.object.quaternion);
                    zoomChanged.value = false;
                    userAction = false;

                    return true;
                }

                return false;
            };
        }();

        this.onUserInteraction = function (event) {
            holdAutoRotateTill = Date.now() + autoRotationDelay;
            userAction = true;
            userActionDamping = true;
        }

        this.setAutoRotationDelay = function (delay) {
            autoRotationDelay = delay * 1000;
            holdAutoRotateTill = Date.now() + autoRotationDelay;
        }
    }

    showSnackBar(message) {
        // clearTimeout(this.snackTimeout);
        // this.snackTimeout = setTimeout(() => {
        //     Q3.showSnackBar(message);
        // }, 200);
    }

    isDamping() {
        const sphericalDelta = this._sphericalDelta;
        const panOffset = this._panoffset;
        const distanceDelta = this._distanceDelta;
        const zoomDelta = this._zoomDelta;

        const epsDamping = 0.05;

        const theta = Math.abs(sphericalDelta.theta) < epsDamping;
        const phi = Math.abs(sphericalDelta.phi) < epsDamping;

        const panX = Math.abs(panOffset.x) < epsDamping;
        const panY = Math.abs(panOffset.y) < epsDamping;
        const panZ = Math.abs(panOffset.z) < epsDamping;

        const dtDelta = Math.abs(distanceDelta.value) < epsDamping;
        const dtDist = Math.abs(zoomDelta.value) < epsDamping;

        return !(theta && phi && panX && panY && panZ && dtDelta && dtDist);
    }

    _pickTarget(target) {
        //let partition = this._scene.getPartition();

        //if (!partition) return;

        // let dir = this.object.getWorldDirection();
        // let pos = this.object.position;

        // setRay(pos, dir, 0, RAYCAST_DISTANCE);

        //let nodes = partition.intersect();


        //if (!nodes || nodes.length <= 0) return;

        // let intersections = Physics.intersectObjectFiltered(this._scene, true);
        // let intersections = raycaster.intersectObject(this._scene, true);

        // const collected = [];
        // this._scene.traverse(x => {
        //     if (this._filter(x)) collected.push(x);
        // });

        const itsct = RenderManager.raycaster.pick(g_itsct.set(0, 0), this.object);
        if (itsct.objectId > 0) {
            target.copy(itsct.point);

            // {
            //     const mesh = new THREE.Mesh(new THREE.BoxBufferGeometry(0.1, 0.1, 0.1), new THREE.MeshBasicMaterial({ color: 0x0000ff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false }));
            //     this._scene.add(mesh);
            //     mesh.position.copy(itsct.point);
            //     mesh.scale.set(0.1,0.1,0.1);
            //     setTimeout(() => this._scene.remove(mesh), 10000);
            // }

            return true;
        }
        return false;

        // {
        //     const mesh = new THREE.Mesh(new THREE.BoxBufferGeometry(0.1, 0.1, 0.1), new THREE.MeshBasicMaterial({ color: 0x0000ff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false }));
        //     this._scene.add(mesh);
        //     mesh.position.copy(dir).multiplyScalar(200).add(pos);
        //     setTimeout(() => this._scene.remove(mesh), 100);
        // }

        // {
        //     const mesh = new THREE.Mesh(new THREE.BoxBufferGeometry(0.1, 0.1, 0.1), new THREE.MeshBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false }));
        //     this._scene.add(mesh);
        //     mesh.position.copy(dir).multiplyScalar(5).add(pos);
        //     setTimeout(() => this._scene.remove(mesh), 100);
        // }

        return false;

        let intersections = raycaster.intersectObjects(collected, true);

        if (!intersections || intersections.length <= 0) return false;

        for (let i = 0; i < intersections.length; i++) {
            let itsct = intersections[i];

            let isVisible = true;
            let parent = itsct.object;

            //Check chain of visibility.
            while (isVisible && parent) {
                isVisible &= parent.visible;
                parent = parent.parent;
            }

            if (!isVisible
                || itsct.object.isGate
                || itsct.object.dontShowInHierarchyPanel
                || itsct.object === this._helper
                || itsct.object.isBackground
            ) continue;

            // if (itsct.distance < MIDPOINT_DISTANCE) {
            //     let midpoint = itsct.point.add(this.object.position).divideScalar(2);
            //     this.target.copy(midpoint);
            // } else {
            //     let point = itsct.point.sub(dir.multiplyScalar(RAYCAST_TARGET_DISTANCE));
            //     this.target.copy(point);
            // }

            return true;
        }

        return false;
    }

    _isForwardVector(normal) {
        if (normal.length() === 0) return true;

        let forward = this.object.getWorldDirection(g_forward);

        return forward.distanceToSquared(normal) < EPS;
    }

    _getDistanceToTargetSq() {
        return this.object.position.distanceToSquared(this.lookTarget ? this.lookTarget : this.target);
    }

    _updateCamera() {
        //this._reverseRaycast();

        let distToTarget = this._getDistanceToTargetSq();
        let near = this.object.near;

        let newNear = Math.max(Math.min(0.1, Math.pow(distToTarget, 2)), MIN_NEAR);

        //newNear = MIN_NEAR;

        if (Math.abs(newNear - near * near) > EPS) {
            this.object.near = newNear;
            //this.object.far = 1 / (-Math.log(newNear));


            //this.object.parent.scale.set(1 / newNear, 1 / newNear, 1 / newNear);

            this.object.updateProjectionMatrix();

            this._helper.scale.set(newNear, newNear, newNear);
            // this._helper.scale.set(0.1, 0.1, 0.1);
        }

        // Debug.setValue("Near", newNear);
        // Debug.setValue("Far", this.object.far);
        // Debug.setValue("Distance", distToTarget);
    }
}

export default PhysicalCameraControls;
export { PhysicalCameraControls };

function setRay(origin, dir, near = 0, far = Infinity) {
    raycaster.set(origin, dir);
    raycaster.near = near;
    raycaster.far = far;
}